'use client'

import React, { startTransition, useActionState, useCallback, useEffect, useRef, useState } from 'react'

import { addHours, format } from 'date-fns'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { debounce } from 'lodash'
import { useParams } from 'next/navigation'

import { ButtonClick, track } from '@dfds-frontend/tracking'
import { BffInterfacesDeparture } from '@dfds-pax/booking-experience-bff'
import { Button, ShipLoader, Switch } from '@dfds-pax/shared-components/client'
import { mapUnloRoute } from '@dfds-pax/utils'

import OfferInvalidModal from '@/app/[locale]/[ferry]/(modals)/OfferInvalidModal'
import { RemoveOfferModal } from '@/app/[locale]/[ferry]/(modals)/RemoveOfferModal'
import { trackRemoveAddons } from '@/app/[locale]/[ferry]/(tracking)/trackAddonCart'
import { trackAddDeparture, trackRemoveDeparture } from '@/app/[locale]/[ferry]/(tracking)/trackDepartureCart'
import Tracking from '@/app/[locale]/[ferry]/(tracking)/Tracking'
import checkBasketValidity, { BasketError } from '@/app/[locale]/[ferry]/_actions/checkBasketValidity'
import { getBasket } from '@/app/[locale]/[ferry]/_actions/getBasket'
import BackendErrorModal from '@/app/[locale]/[ferry]/_components/errors/BackendErrorModal'
import { logFrontendError } from '@/app/[locale]/[ferry]/_lib/frontendLogging'
import { removePets } from '@/app/[locale]/[ferry]/pets/_actions/removePets'
import { LegSkeleton } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure)/(skeleton)/LegSkeleton'
import { JourneySwitch } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure)/JourneySwitch'
import { RouteFilter } from '@/app/[locale]/[ferry]/yourbooking/(yourbooking)/(departure-filters)/RouteFilters'
import { addDeparture } from '@/app/[locale]/[ferry]/yourbooking/_actions/addDeparture'
import { addDummyDeparture } from '@/app/[locale]/[ferry]/yourbooking/_actions/addDummyDeparture'
import { addTwoWayDummyDeparture } from '@/app/[locale]/[ferry]/yourbooking/_actions/addTwoWayDummyDeparture'
import { getDepartures } from '@/app/[locale]/[ferry]/yourbooking/_actions/getDepartures'
import { postYourBooking } from '@/app/[locale]/[ferry]/yourbooking/_actions/postYourBooking'
import { readdDepartureWithoutOffer } from '@/app/[locale]/[ferry]/yourbooking/_actions/readdDepartureWithoutOffer'
import { removeReturnDeparture } from '@/app/[locale]/[ferry]/yourbooking/_actions/removeReturnDeparture'
import { isFootPassenger } from '@/app/lib/vehicleHelpers'
import { useBasket } from '@/providers/basket-context'
import { departuresBusyAtom, loadingAtom } from '@/states/loadingAtoms'
import { Departure } from '@/states/selection.interface'
import {
  selectedAddonsAtom,
  selectedDateAtom,
  selectedDeparturesAtom,
  selectedOfferAtom,
  selectedPassengersAtom,
  selectedPetsAtom,
  selectedRoutesAtom,
  selectedVehiclesAtom,
} from '@/states/selectionAtom'
import EditIcon from '@/ui-cvi/components/dxp-icons/system/EditIcon'

import {
  getUrlLocale,
  hasHomeButNotOut,
  hasNeitherOutOrHome,
  hasNotOut,
  hasOutButNotHome,
  hasOverlapping,
  isDateInThePast,
  SingleTierRoutes,
} from '../../utils'
import { scrollToCallback, scrollToElementAfterLayoutSettles } from '../../utils/scrollHelpers'

import { useTrackOverlapping } from '../../hooks/useTrackOverlapping'
import { useWindowWidth } from '../../hooks/useWindowWidth'
import { useTranslation } from '@/hooks/useDebugTranslations'

import Banner from './banner/banner'
import DepartureLeg from './departures/departure-leg/DepartureLeg'
import DepartureLegSingleTier from './departures/departure-leg/DepartureLegSingleTier'
import { DepartureSelectionsAlert } from './DepartureSelectionsAlert'
import OnewayModal from './modals/OneWayModal'
import OverlappingModal, { OverlappingModel } from './modals/OverlappingModal'
import RecoveredBasketInfoModal from './modals/RecoveredBasketInfoModal'
import { buildDepartureArgs, dateChanged, DepartureFunctions } from './YourBookingHandlers'

const YourbookingDepartures = (): React.ReactElement => {
  const { t } = useTranslation()
  const { widthSmallerThan768 } = useWindowWidth()

  const [outboundAccordionOpen, setOutboundAccordionOpen] = useState<boolean>(false)
  const [returnAccordionOpen, setReturnAccordionOpen] = useState<boolean>(false)

  const [contentReady, setContentReady] = useState<boolean>(false)
  const [validationMessage, setValidationMessage] = useState<string>('')
  const [showReturnScrollSpace, setShowReturnScrollSpace] = useState<boolean>(false)
  const [showDepartureUnavailableBanner, setShowDepartureUnavailableBanner] = useState<boolean>(false)
  const clearValidationMessage = useCallback(() => setValidationMessage(''), [])

  const outboundLeg = useRef<HTMLDivElement | null>(null)
  const returnLeg = useRef<HTMLDivElement | null>(null)

  const [depDateHomeFallback, setDepDateHomeFallback] = useState<string | null>(null)
  const [routeHomeFallback, setRouteHomeFallback] = useState<string | null>(null)
  const [outboundData, setOutboundData] = useState<BffInterfacesDeparture.Departure[]>()
  const [returnData, setReturnData] = useState<BffInterfacesDeparture.Departure[]>()
  const [previousDepartureId, setPreviousDepartureId] = useState<string | undefined>()
  const [loading, setLoading] = useState<boolean>(false)
  const [modelForOverlappingModal, setModelForOverlappingModal] = useState<OverlappingModel>()
  const [showOnewayModal, setShowOnewayModal] = useState<boolean>(false)
  const [showBasketRecoveredInfoModal, setShowBasketRecoveredInfoModal] = useState<boolean>(false)
  const [showOfferInvalidModal, setShowOfferInvalidModal] = useState<boolean>(false)
  const [showOfferInfoBanner, setShowOfferInfoBanner] = useState<boolean>(true)
  const [basketError, setBasketError] = useState<BasketError | null>(null)

  const [errorCorrelationId, setErrorCorrelationId] = useState<string | null>(null)
  if (errorCorrelationId) {
    throw new Error(errorCorrelationId)
  }

  const setIsLoadingState = useSetAtom(loadingAtom)
  const setDeparturesBusy = useSetAtom(departuresBusyAtom)

  // Expose the local departure-loading state so the shared Continue button can
  // stay disabled until the departure mutation has been committed to the basket.
  useEffect(() => {
    setDeparturesBusy(loading)
    return () => setDeparturesBusy(false)
  }, [loading, setDeparturesBusy])

  const [selectedAddons, setSelectedAddons] = useAtom(selectedAddonsAtom)
  const [selectedPets, setSelectedPets] = useAtom(selectedPetsAtom)
  const selectedVehicles = useAtomValue(selectedVehiclesAtom)
  const [selectedOffer, setSelectedOffer] = useAtom(selectedOfferAtom)

  const [selectedRoutes, setSelectedRoutes] = useAtom(selectedRoutesAtom)
  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom)
  const selectedPassengers = useAtomValue(selectedPassengersAtom)
  const [selectedDepartures, setSelectedDepartures] = useAtom(selectedDeparturesAtom)

  const [selectedRouteFilters, setSelectedRouteFilters] = useState<RouteFilter[]>([
    { frcqf: true, frdkk: true },
    { frcqf: true, frdkk: true },
  ])

  const params = useParams()
  const locale = (params?.locale as string) || ''
  const ferry = (params?.ferry as string) || ''

  const {
    basket,
    basketId,
    basketInitiated,
    basketLoading,
    commercialDiscount,
    depDateHomebound,
    recoveredBasketErrors,
    residentSwitchLoading,
    residentSwitchNonce,
    routeHomebound,
    toggleSogResidentVehicle,
    updateBasket,
  } = useBasket()

  // Snapshot the current basket for the departure tracking events (strangling jotai out of the tracker; mirrors getTrackingData).
  const buildDepartureTrackingContext = () => ({
    adults: basket?.passengers.filter((passenger) => passenger.categoryCode.toUpperCase() === 'ADULT').length ?? 0,
    childrenAges:
      basket?.passengers
        .filter((passenger) => ['CHILD', 'INFANT'].includes(passenger.categoryCode.toUpperCase()))
        .map((passenger) => passenger.age ?? 0) ?? [],
    isReturnBooking: basket?.departures.length === 2,
    petCount: basket?.departures[0]?.pets.reduce((total, pet) => total + pet.quantity, 0) ?? 0,
    trailerCode: basket?.vehicle?.trailerTypeCode,
    vehicleCode: basket?.vehicle?.vehicleTypeCode ?? 'NCAR',
  })

  // Basket-sourced add-on removal tracking (replaces the jotai selectedAddonsAtom read in trackRemoveAddons).
  const trackRemoveLegAddons = (isReturnLeg: boolean) => {
    const departure = basket?.departures[isReturnLeg ? 1 : 0]
    if (!departure) return
    trackRemoveAddons(
      isReturnLeg,
      {
        arrivalPort: departure.arrivalPort,
        currency: basket?.priceStructure.currency ?? '',
        departureDate: departure.departureDate,
        departurePort: departure.departurePort,
        departureTime: departure.departureTime,
        offerCode: departure.offer?.code,
        productCode: departure.productCode,
      },
      (departure.addons ?? []).map((addon) => ({
        addonCode: addon.addonCode,
        basePrice: addon.unitPriceStructure.basePrice,
        offerPrice: addon.unitPriceStructure.offer?.price ?? null,
        quantity: addon.quantity,
      })),
      buildDepartureTrackingContext()
    )
  }

  const hasReturnRequiringOffer = Boolean(selectedOffer?.offerRLSHours && selectedOffer.offerRLSHours > 0)

  const [state, formAction] = useActionState(
    postYourBooking.bind(null, locale, ferry, basketId, selectedPets.showNumberOfPets > 0, hasReturnRequiringOffer),
    {
      success: false,
    }
  )
  const [showRemoveOfferModal, setShowRemoveOfferModal] = useState<boolean>(false)

  useEffect(() => {
    if (state?.requiresOfferRemoval) {
      setShowRemoveOfferModal(true)
    }
    if (state?.basketError) {
      // postYourBooking failed and we stay on the page, so release the Continue button spinner
      window.dispatchEvent(new Event('continueAborted'))
    }
  }, [state])

  const departureClicked: DepartureFunctions['DepartureClicked'] = async (
    clickedDepartureId,
    legNumber,
    clickedProductCode,
    continueClikedFromOverlappingModal
  ) => {
    const basket = await getBasket(basketId)
    const initialBasketValidity = await checkBasketValidity('DEPARTURES', basket)
    if (!initialBasketValidity.isValid) {
      setBasketError(initialBasketValidity)
      setDeparturesBusy(false)
      return
    }

    const departure =
      legNumber === 1
        ? outboundData?.find((x) => x.departureId === clickedDepartureId)
        : returnData?.find((x) => x.departureId === clickedDepartureId)

    if (!departure) {
      setDeparturesBusy(false)
      return
    }

    const selectedTicket: BffInterfacesDeparture.Ticket = departure.tickets.filter((t) => t.productCode === clickedProductCode)[0]
    const departuresOverlapping =
      (legNumber === 1 &&
        selectedDepartures?.departureHome?.checkinDateTime &&
        selectedDepartures.departureHome.checkinDateTime <= departure.arrivalDateTime) ||
      (legNumber === 2 &&
        selectedDepartures?.departureOut?.arrivalDateTime &&
        selectedDepartures?.departureOut?.arrivalDateTime >= departure.checkinDateTime)

    const offerDepartures = selectedTicket.ticketOffer
      ? {
          home: selectedDepartures.departureHome?.offer ?? undefined,
          out: selectedDepartures.departureOut?.offer ?? undefined,
        }
      : { home: undefined, out: undefined }

    if (departuresOverlapping && !continueClikedFromOverlappingModal) {
      setModelForOverlappingModal({
        departureId: clickedDepartureId,
        legNumber,
        productCode: clickedProductCode,
        show: true,
      })
      track<ButtonClick>({
        clickedElementText: `${legNumber === 1 ? 'outbound' : 'return'}`,
        event: 'buttonClick',
        eventAction: `overlappingDepartureSelected`,
      })
    } else {
      setLoading(true)

      let { departureOut } = selectedDepartures
      const { departureHome } = selectedDepartures
      const selected = legNumber === 1 ? departureOut?.departureDateTimeForBasket : departureHome?.departureDateTimeForBasket
      const laterDepartureSelected = selected ? new Date(departure.departureTimeForBasket) > new Date(selected) : false
      const bodyOffset = Math.abs(document.body.getBoundingClientRect().top)
      const departureError = await addDeparture(basketId, legNumber, departure, selectedTicket, locale, basket.sessionId)
      if (departureError) {
        if ('errorType' in departureError) {
          setErrorCorrelationId(departureError.correlationId)
          return
        }
        setBasketError(departureError)
        setLoading(false)
        return
      }
      if (selectedPets.pets.length > 0) {
        const petBasketError = await removePets(basketId)
        if (petBasketError) {
          setBasketError(petBasketError)
          setLoading(false)
          return
        }
      }
      if (legNumber === 1) {
        setLoading(true)
        const departureDate =
          selectedDate.returnDepartureDate && selectedDate.returnDepartureDate >= departure.departureTimeForBasket.split('T')[0]
            ? selectedDate.returnDepartureDate
            : selectedDate.outboundDepartureDate
        setDepDateHomeFallback(departureDate)
        const response = await getDepartures({
          date: departureDate as string,
          ...buildDepartureArgs(
            2,
            selectedRoutes,
            !selectedOffer.offerRLSHours || (!!selectedOffer.offerRLSHours && offerDepartures.out) ? selectedOffer : {},
            selectedVehicles,
            selectedPassengers,
            clickedDepartureId
          ),
        })
        setReturnData(response?.departures)
        setTimeout(() => {
          setLoading(false)
        }, 500)
      }

      //When we have a selected rls offer outbound but selected a return without a offer, we reselect the outbound without the offer applied.
      if (legNumber === 2 && selectedOffer.offerRLSHours && selectedDepartures.departureOut?.offer && !selectedTicket.ticketOffer) {
        const outboundDeparture = outboundData?.find((d) => d.departureId === selectedDepartures.departureOut?.id)
        const outboundTicket = outboundDeparture?.tickets.find((t) => t.productCode === selectedDepartures.departureOut?.productCode)
        if (outboundDeparture && outboundTicket) {
          const readdError = await readdDepartureWithoutOffer(basket!.id, getUrlLocale())
          if (readdError) {
            setBasketError(readdError)
            setLoading(false)
            return
          }
          departureOut = {
            ...departureOut,
            offer: undefined,
            price: outboundTicket.price,
          } as Departure
          setSelectedOffer({})
          const outboundResponse = await getDepartures({
            date: selectedDate.outboundDepartureDate as string,
            ...buildDepartureArgs(1, selectedRoutes, {}, selectedVehicles, selectedPassengers, selectedDepartures.departureOut.id),
          })
          setOutboundData(outboundResponse?.departures)
          const homeboundResponse = await getDepartures({
            date: selectedDate.returnDepartureDate as string,
            ...buildDepartureArgs(2, selectedRoutes, {}, selectedVehicles, selectedPassengers, selectedDepartures.departureHome?.id),
          })
          setOutboundData(outboundResponse?.departures)
          setReturnData(homeboundResponse?.departures)
        }
      }

      if (legNumber === 1 && selectedOffer.offerRLSHours) {
        setShowOfferInfoBanner(true)
      }
      // Tracks whether the return removal was already emitted so the later outbound-moved-past-return
      // block doesn't emit duplicate removals from the same pre-change snapshots.
      let returnRemovalTracked = false
      if (
        (selectedOffer.offerRLSHours && legNumber === 1 && selectedDepartures.departureHome?.id) || //We always deselect the return if we have a selected rls offer on the outbound
        departuresOverlapping ||
        (legNumber === 1 &&
          selectedDate.returnDepartureDate &&
          selectedDate.outboundDepartureDate &&
          selectedDate?.returnDepartureDate < selectedDate?.outboundDepartureDate &&
          selectedDepartures?.departureHome?.id)
      ) {
        const returnDepartureError = await removeReturnDeparture(basketId)
        if (returnDepartureError) {
          setBasketError(returnDepartureError)
          setLoading(false)
          return
        }
        trackRemoveDeparture(true, selectedDepartures.departureHome, buildDepartureTrackingContext())
        trackRemoveLegAddons(true)
        returnRemovalTracked = true
        const _basketError = await addDummyDeparture(
          basketId,
          legNumber === 1 ? selectedRoutes.homebound : selectedRoutes.outbound,
          legNumber === 1
            ? selectedDate.returnDepartureDate &&
              selectedDate.outboundDepartureDate &&
              selectedDate.returnDepartureDate < selectedDate.outboundDepartureDate
              ? selectedDate.outboundDepartureDate
              : selectedDate.returnDepartureDate
            : selectedDate.outboundDepartureDate,
          legNumber === 1 ? 2 : 1,
          locale,
          basket?.sessionId
        )
        if (_basketError) {
          setBasketError(_basketError)
          return
        }
      }

      // Clear addons between selected departures, to not show any wrong addons before new one are loaded
      if (legNumber === 1) {
        setSelectedRoutes({
          ...selectedRoutes,
          outbound: mapUnloRoute(`${departure.departurePortCode}-${departure.arrivalPortCode}`),
        })
        trackRemoveLegAddons(false)
        setSelectedAddons({ ...selectedAddons, outboundAddons: [] })
      } else {
        setSelectedRoutes({
          ...selectedRoutes,
          homebound: mapUnloRoute(`${departure.departurePortCode}-${departure.arrivalPortCode}`),
        })
        trackRemoveLegAddons(true)
        setSelectedAddons({ ...selectedAddons, returnAddons: [] })
      }

      // Clear pets between selected departures, to not show any wrong pets before new one are loaded
      // Further this is also to avoid issues with pets having different prices on outbound and homebound.
      setSelectedPets({ ...selectedPets, pets: [] })

      setValidationMessage('') // Clear message
      trackRemoveDeparture(
        legNumber === 2,
        legNumber === 2 ? selectedDepartures.departureHome : selectedDepartures.departureOut,
        buildDepartureTrackingContext()
      )

      const newDeparture: Departure = {
        arrivalDateTime: departure.arrivalDateTime,
        arrivalPortCode: departure.arrivalPortCode,
        checkinDateTime: departure.checkinDateTime,
        currencyCode: departure.tickets[0].currency,
        departureDate: departure.departureDate,
        departureDateTimeForBasket: departure.departureTimeForBasket,
        departurePortCode: departure.departurePortCode,
        departurePrice: selectedTicket?.ticketOffer?.price ?? selectedTicket?.price,
        departureTime: departure.departureTime,
        id: clickedDepartureId,
        listPosition: (legNumber === 1 ? outboundData : returnData)?.findIndex((d) => d.departureId === clickedDepartureId) ?? -1,
        offer: selectedTicket.ticketOffer,
        productCode: selectedTicket.productCode,
      }

      if (legNumber === 1) {
        selectedTicket.ticketOffer &&
          selectedTicket.ticketOffer.rlsHours &&
          setSelectedOffer({
            ...selectedOffer,
            offerRLSHours: selectedTicket.ticketOffer.rlsHours,
          })
        setSelectedDepartures({
          departureHome:
            departuresOverlapping || (selectedOffer.offerRLSHours && departureHome?.id) ? null : selectedDepartures?.departureHome,
          departureOut: newDeparture,
        })
        setShowDepartureUnavailableBanner(false)
        setSelectedDate({
          outboundDepartureDate: departure.departureDate,
          returnDepartureDate: !selectedDate.returnDepartureDate
            ? null
            : departure.departureDate > selectedDate.returnDepartureDate
              ? departure.departureDate
              : selectedDate.returnDepartureDate,
        })
      }
      if (legNumber === 2) {
        setShowReturnScrollSpace(false)
        setSelectedDepartures({
          departureHome: newDeparture,
          departureOut: departuresOverlapping ? null : departureOut,
        })
      }

      // Pets were cleared before this add commits; report zero rather than the stale pre-removal count.
      trackAddDeparture(legNumber === 2, newDeparture, { ...buildDepartureTrackingContext(), petCount: 0 })
      setPreviousDepartureId(`${clickedDepartureId}`)

      const currentElement = document.getElementById(clickedDepartureId)

      // Scroll desktop device
      if (!widthSmallerThan768 && clickedDepartureId && laterDepartureSelected && previousDepartureId) {
        const previousElement = document.getElementById(previousDepartureId)
        if (currentElement && previousElement) {
          const departureRowHeaderHeight = 108
          const previousElementHeight = previousElement.clientHeight
          const scrollTo = bodyOffset - (previousElementHeight - departureRowHeaderHeight)
          window.scroll(0, scrollTo)
        }
      }

      if (
        legNumber === 1 &&
        selectedDepartures?.departureHome?.departureDateTimeForBasket &&
        selectedDepartures.departureHome.departureDateTimeForBasket < departure.departureTimeForBasket
      ) {
        const returnDepartureError = await removeReturnDeparture(basketId)
        if (returnDepartureError) {
          setBasketError(returnDepartureError)
          setLoading(false)
          return
        }
        if (!returnRemovalTracked) {
          trackRemoveDeparture(true, selectedDepartures.departureHome, buildDepartureTrackingContext())
          trackRemoveLegAddons(true)
        }
      }

      updateBasket()
      setTimeout(() => {
        setLoading(false)
      }, 500)
    }
  }

  // User selects oneway
  const handleOneLeg = async () => {
    const basket = await getBasket(basketId)
    const initialBasketValidity = await checkBasketValidity('DEPARTURES', basket)
    if (!initialBasketValidity.isValid) {
      setBasketError(initialBasketValidity)
      return
    }

    const returnDepartureError = await removeReturnDeparture(basketId)
    if (returnDepartureError) {
      setBasketError(returnDepartureError)
      return
    }
    trackRemoveDeparture(true, selectedDepartures.departureHome, buildDepartureTrackingContext())
    trackRemoveLegAddons(true)
    selectedDate &&
      setSelectedDate({
        outboundDepartureDate: selectedDate.outboundDepartureDate,
        returnDepartureDate: null,
      })
    selectedRoutes &&
      setSelectedRoutes({
        homebound: null,
        outbound: selectedRoutes.outbound,
      })
    selectedDepartures &&
      setSelectedDepartures({
        departureHome: null,
        departureOut: selectedDepartures.departureOut,
      })
    updateBasket()
    setValidationMessage('') // Clear message
  }

  // User selects two way
  const handleTwoLegs = async () => {
    track<ButtonClick>({
      clickedElementText: 'return',
      event: 'buttonClick',
      eventAction: 'DepartureOnewayReturnClicked',
    })
    const basket = await getBasket(basketId)
    const initialBasketValidity = await checkBasketValidity('DEPARTURES', basket)
    if (!initialBasketValidity.isValid) {
      setBasketError(initialBasketValidity)
      return
    }

    if (!depDateHomeFallback) setDepDateHomeFallback(selectedDate.outboundDepartureDate)
    if (selectedOffer.offerRLSHours) {
      const departure = outboundData?.find((d) => d.departureId === selectedDepartures.departureOut?.id)
      const ticket = departure?.tickets.find((t) => t.productCode === selectedDepartures.departureOut?.productCode)
      if (departure && ticket && ticket.ticketOffer) {
        const departureError = await addDeparture(basketId, 1, departure, ticket, locale, basket.sessionId)
        if (departureError) {
          if ('errorType' in departureError) {
            setErrorCorrelationId(departureError.correlationId)
            return
          }
          setBasketError(departureError)
          return
        }
        if (selectedPets.pets.length > 0) {
          const petBasketError = await removePets(basketId)
          if (petBasketError) {
            setBasketError(petBasketError)
            return
          }
        }
        setSelectedDepartures({
          departureHome: null,
          departureOut: {
            ...selectedDepartures.departureOut,
            offer: ticket.ticketOffer,
            price: ticket.ticketOffer.price,
          } as Departure,
        })

        const response = await getDepartures({
          date: depDateHomeFallback as string,
          ...buildDepartureArgs(
            2,
            selectedRoutes,
            selectedOffer,
            selectedVehicles,
            selectedPassengers,
            selectedDepartures?.departureOut?.id
          ),
        })
        setReturnData(response?.departures)
      }
    }
    const _basketError = await addTwoWayDummyDeparture(
      basketId,
      locale,
      routeHomeFallback,
      depDateHomeFallback,
      selectedDate?.outboundDepartureDate,
      selectedRoutes.outbound,
      basket?.sessionId
    )
    if (_basketError) {
      setBasketError(_basketError)
      return
    }
    selectedDate &&
      setSelectedDate({
        outboundDepartureDate: selectedDate.outboundDepartureDate,
        returnDepartureDate: depDateHomeFallback ?? selectedDate.outboundDepartureDate,
      })
    selectedRoutes &&
      setSelectedRoutes({
        homebound: routeHomeFallback ?? `${selectedRoutes.outbound?.slice(2, 4)}${selectedRoutes.outbound?.slice(0, 2)}`,
        outbound: selectedRoutes.outbound,
      })
    if (!returnData) {
      const response = await getDepartures({
        date: (depDateHomeFallback ?? selectedDate.outboundDepartureDate) as string,
        ...buildDepartureArgs(
          2,
          selectedRoutes,
          !selectedOffer.offerRLSHours || (!!selectedOffer.offerRLSHours && selectedDepartures.departureOut?.offer) ? selectedOffer : {},
          selectedVehicles,
          selectedPassengers
        ),
      })
      setReturnData(response?.departures)
    }
    !outboundAccordionOpen && setReturnAccordionOpen(true)
    updateBasket()
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceDateChanges = useCallback(
    // eslint-disable-next-line react-hooks/use-memo
    debounce(
      (departureDate: string, legNumber?: 1 | 2) =>
        legNumber &&
        dateChanged(
          departureDate,
          legNumber,
          setLoading,
          setOutboundData,
          setReturnData,
          depDateHomeFallback,
          setDepDateHomeFallback,
          selectedRoutes,
          selectedOffer,
          selectedVehicles,
          selectedPassengers,
          selectedDate,
          selectedDepartures,
          setSelectedDate,
          setIsLoadingState
        ),
      500,
      {
        leading: true,
        trailing: true,
      }
    ),
    [selectedVehicles, selectedPassengers, selectedDepartures, selectedOffer]
  )

  const fetchDepartures = async () => {
    const response = await getDepartures({
      date: selectedDate.outboundDepartureDate as string,
      ...buildDepartureArgs(1, selectedRoutes, selectedOffer, selectedVehicles, selectedPassengers),
    })
    if (response?.invalidOfferCode) {
      logFrontendError('Invalid offer', {
        basketId,
        leg: 'outbound',
        offerCode: selectedOffer.offerCode,
        page: window.location.pathname,
        sessionId: basket?.sessionId,
      })
      setShowOfferInvalidModal(true)
      return
    }
    setOutboundData(response?.departures)

    //Only call this if we already have a selected return
    if (selectedDate.returnDepartureDate && selectedDepartures.departureOut?.id) {
      const returnResponse = await getDepartures({
        date: selectedDate.returnDepartureDate as string,
        ...buildDepartureArgs(
          2,
          selectedRoutes,
          !selectedOffer.offerRLSHours || (!!selectedOffer.offerRLSHours && selectedDepartures.departureOut.offer) ? selectedOffer : {},
          selectedVehicles,
          selectedPassengers,
          selectedDepartures?.departureOut?.id
        ),
      })
      if (returnResponse?.invalidOfferCode) {
        logFrontendError('Invalid offer', {
          basketId,
          leg: 'return',
          offerCode: selectedOffer.offerCode,
          page: window.location.pathname,
          sessionId: basket?.sessionId,
        })
        setShowOfferInvalidModal(true)
        return
      }
      setReturnData(returnResponse?.departures)
    }
  }

  const initialize = async () => {
    await fetchDepartures()

    setLoading(true)
    // Returning with a stale selection: past dates reset to minibook; same-day departures
    // that have crossed their sales cutoff or sailed are deselected below for replacement.
    const selectedDateInPast =
      !!selectedDepartures.departureOut?.departureDate && isDateInThePast(selectedDepartures.departureOut.departureDate)
    if (selectedDateInPast) {
      // If date in past we go automatic to minibook
      // We use location href when going back to minibook
      window.location.href = location.pathname.split('/').slice(0, -1).join('/')
      return
    }

    let departureOut = selectedDepartures.departureOut
    if (departureOut?.id) {
      try {
        const currentBasket = await getBasket(basketId)
        const validity = await checkBasketValidity('DEPARTURES', currentBasket)
        if (!validity.isValid && validity.type === 'SALESCLOSED') {
          const departureError = await addDummyDeparture(
            currentBasket.id,
            selectedRoutes.outbound,
            selectedDate.outboundDepartureDate,
            1,
            locale,
            currentBasket.sessionId
          )
          if (departureError) {
            setBasketError(departureError)
          } else {
            trackRemoveDeparture(false, departureOut, buildDepartureTrackingContext())
            trackRemoveLegAddons(false)
            setSelectedDepartures({ ...selectedDepartures, departureOut: null })
            setSelectedAddons({ ...selectedAddons, outboundAddons: [] })
            setShowDepartureUnavailableBanner(true)
            departureOut = null

            const petError = selectedPets.pets.length > 0 ? await removePets(basketId) : null
            if (petError) {
              setBasketError(petError)
            } else {
              setSelectedPets({ ...selectedPets, pets: [] })
            }
          }
        }
      } catch (error) {
        logFrontendError('Failed to recover closed departure', {
          basketId,
          errorMessage: error instanceof Error ? error.message : String(error),
          sessionId: basket?.sessionId,
        })
        setBasketError({ basketId, isValid: false, type: 'UNKNOWNERROR' })
      }
    }

    if (departureOut?.id) {
      setOutboundAccordionOpen(false)
    } else {
      depDateHomebound && setDepDateHomeFallback(depDateHomebound)
      setOutboundAccordionOpen(true)
    }

    if (selectedDepartures?.departureHome?.id) {
      setReturnAccordionOpen(false)
    } else {
      routeHomebound && setRouteHomeFallback(routeHomebound)
      setReturnAccordionOpen(true)
    }
    setShowBasketRecoveredInfoModal(
      (!!recoveredBasketErrors?.addon && recoveredBasketErrors.addon.length > 0) ||
        (!!recoveredBasketErrors?.dates && recoveredBasketErrors.dates.length > 0) ||
        (!!recoveredBasketErrors?.departures && recoveredBasketErrors.departures.length > 0) ||
        (!!recoveredBasketErrors?.price && recoveredBasketErrors.price.length > 0)
    )

    setTimeout(() => {
      setLoading(false)
      setContentReady(true)
    }, 500)
  }

  useEffect(() => {
    ;(async () => {
      if (basketInitiated) {
        await initialize()
        updateBasket()
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basketInitiated])

  // Re-fetch the departures list when the vehicle changes in place, or after a resident-vehicle
  // toggle rebuilt the basket, so the rows show the prices for the new commercial discount.
  useEffect(() => {
    if (!contentReady) return
    fetchDepartures()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicles.vehicleCode, selectedVehicles.trailerCode, commercialDiscount])

  const rlsOfferDateEligible = !!returnData?.some((d) => d.tickets.some((t) => t.ticketOffer))
  // Only used to re-open the dismissed banner when eligibility flips.
  const previousRlsOfferDateEligible = useRef<boolean | undefined>(undefined)

  useEffect(() => {
    if (returnData && selectedOffer.offerCode && selectedOffer.offerRLSHours && selectedDate.returnDepartureDate) {
      if (previousRlsOfferDateEligible.current !== rlsOfferDateEligible) {
        setShowOfferInfoBanner(true)
      }
      previousRlsOfferDateEligible.current = rlsOfferDateEligible
    } else {
      setShowOfferInfoBanner(false)
      previousRlsOfferDateEligible.current = undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartures, selectedDate, returnData])

  useTrackOverlapping(selectedDepartures)

  // Validate that the required departures are selected before the yourbooking action runs.
  // We always prevent the native submit and only invoke the server action manually when the
  // selection is valid. Keeping the action off the <form> avoids React invoking it on an
  // invalid submit (which would redirect away before the validation message can show).
  // On failure we surface a message, open the relevant accordion and release the shared
  // Continue button spinner via the 'continueAborted' event.
  const handleDepartureFormSubmit: React.SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    clearValidationMessage()

    const abortContinue = () => window.dispatchEvent(new Event('continueAborted'))
    // Collapse outbound and expand return in the same render, then scroll once the
    // transition has settled so the two animations don't fight each other.
    const openReturnAccordionAndScroll = () => {
      setShowReturnScrollSpace(true)
      setOutboundAccordionOpen(false)
      setReturnAccordionOpen(true)
      scrollToElementAfterLayoutSettles('returnJourneyPrompt')
    }

    // Oneway
    if (!selectedDate?.returnDepartureDate) {
      if (hasNotOut(selectedDepartures)) {
        setValidationMessage(t('VALIDATION-DEPARTURES-OUTBOUND-MESSAGE'))
        abortContinue()
        scrollToCallback(0, () => {
          setOutboundAccordionOpen(true)
        })
        return
      }
    }
    // Both ways
    else if (hasOutButNotHome(selectedDepartures)) {
      // We show banner if outbound accordion was already closed when you click continue
      if (!outboundAccordionOpen) {
        setValidationMessage(t('VALIDATION-DEPARTURES-RETURN-MESSAGE'))
      }
      abortContinue()
      openReturnAccordionAndScroll()
      return
    } else if (hasHomeButNotOut(selectedDepartures)) {
      // We show banner if return accordion was already closed when you click continue
      if (!returnAccordionOpen) {
        setValidationMessage(t('VALIDATION-DEPARTURES-OUTBOUND-MESSAGE'))
      }
      abortContinue()
      scrollToCallback(0, () => {
        setOutboundAccordionOpen(true)
        setReturnAccordionOpen(false)
      })
      return
    } else if (hasNeitherOutOrHome(selectedDepartures)) {
      setValidationMessage(t('VALIDATION-DEPARTURES-MESSAGE'))
      abortContinue()
      scrollToCallback(0, () => {
        setOutboundAccordionOpen(true)
        setReturnAccordionOpen(false)
      })
      return
    } else if (hasOverlapping(selectedDepartures)) {
      setValidationMessage(t('VALIDATION-DEPARTURES-OVERLAP-MESSAGE'))
      abortContinue()
      scrollToCallback(0, () => {
        setOutboundAccordionOpen(true)
        setReturnAccordionOpen(false)
      })
      return
    }

    // Selection is valid → run the yourbooking server action.
    startTransition(() => {
      formAction()
    })
  }

  if (locale === 'fr-ma' && (!contentReady || !basket)) return <ShipLoader />

  // Show the page skeleton while the basket is (re)loading, and also while the resident-vehicle
  // toggle rebuilds the basket in the background (residentSwitchLoading is scoped to the toggle so
  // other basket refreshes don't trigger a full-page skeleton).
  if (!contentReady || !basket || residentSwitchLoading) {
    return (
      <div className="bg-semantic-global-background-light-grey flex flex-col">
        <div className="p-semantic-global-md md:p-semantic-global-lg col-span-12 lg:col-span-8 lg:p-0">
          <LegSkeleton />
          {selectedDate?.returnDepartureDate ? <LegSkeleton returnLeg={true} /> : null}
        </div>
      </div>
    )
  }

  const showResidentVehicleSwitch =
    locale !== 'fr-ma' &&
    (basket.departures[0].departurePort === 'ESCEU' || basket.departures[0].arrivalPort === 'ESCEU') &&
    basket.vehicle &&
    !isFootPassenger(basket.vehicle.vehicleTypeCode)

  const rlsHours = selectedOffer.offerRLSHours ?? 0
  const rlsInDays = rlsHours > 120
  const rlsOfferDuration = rlsInDays ? Math.floor(rlsHours / 24) : rlsHours
  const rlsInfoKey = rlsInDays ? 'RLS-OFFER-BANNER-INFO-DAYS' : 'RLS-OFFER-BANNER-INFO-HOURS'
  const rlsResetDateKey = rlsInDays ? 'RLS-OFFER-BANNER-INFO-DAYS-RESET-DATE' : 'RLS-OFFER-BANNER-INFO-HOURS-RESET-DATE'

  const showRlsBanner =
    showOfferInfoBanner && !!selectedDate?.returnDepartureDate && !!selectedOffer.offerCode && !!selectedOffer.offerRLSHours
  const showRlsNoOfferSelectedBanner =
    showRlsBanner && !!selectedDepartures.departureOut?.id && !selectedDepartures.departureOut?.offer?.code
  const showRlsReturnDateBanner = showRlsBanner && !!returnData && !!selectedDepartures.departureOut?.offer?.code

  return (
    <form data-e2e="yourbooking-page" id="yourbookingForm" onSubmit={handleDepartureFormSubmit}>
      {showResidentVehicleSwitch ? (
        <div className="lg:pb-semantic-global-lg pt-semantic-global-md md:px-semantic-global-lg px-semantic-global-md lg:p-0">
          <div className="typography-heading-md">{t('ARE-YOU-A-CEUTA-RESIDENT')}</div>
          <div className="pt-semantic-global-md">
            {/*
              key uses residentSwitchNonce so the switch remounts and reverts when a toggle fails.
              The Switch is controlled but keeps optimistic internal state and only re-syncs when its
              `checked` value changes; on failure the basket/`checked` is unchanged, so the remount is
              what snaps it back to the true state.
            */}
            <Switch
              key={`resident-vehicle-switch-${residentSwitchNonce}`}
              checked={commercialDiscount === 'resident'}
              disabled={basketLoading}
              name="resident-vehicle-subsidy-switch"
              label={t('RESIDENT-VEHICLE-DISCOUNT')}
              onChange={async (checked) => {
                track<ButtonClick>({
                  clickedElementText: '',
                  event: 'buttonClick',
                  eventAction: `Resident vehicle toggle enabled ${checked ? 'true' : 'false'} Clicked`,
                })
                const residentBasketError = await toggleSogResidentVehicle(checked)
                if (residentBasketError) setBasketError(residentBasketError)
              }}
            />
          </div>
        </div>
      ) : null}
      <section className="bg-semantic-global-background-light-grey flex flex-col">
        <div id="outboundAccordion" />
        {showDepartureUnavailableBanner ? (
          <div className="pb-semantic-global-sm" data-e2e="departure-unavailable-banner">
            <Banner title={t('SELECT-DEPARTURE-TIME')} bodyText={t('SELECTED-DEPARTURE-UNAVAILABLE')} type="info" />
          </div>
        ) : null}
        <div className="p-semantic-global-md md:p-semantic-global-lg col-span-12 lg:col-span-8 lg:p-0">
          {/* Outbound Departure */}
          {!SingleTierRoutes.includes(selectedRoutes.outbound.toUpperCase()) && outboundData && selectedDate?.outboundDepartureDate ? (
            <div data-e2e="OutboundLeg">
              <DepartureLeg
                dateChanged={debounceDateChanges}
                departureClicked={departureClicked}
                departureDate={selectedDate.outboundDepartureDate}
                departures={outboundData}
                editElement={
                  !outboundAccordionOpen ? (
                    <Button
                      variant="ghostPrimary"
                      aria-label="Edit outbound departures"
                      data-e2e="edit-outbound-leg"
                      onClick={() => setOutboundAccordionOpen(true)}
                      type="button"
                    >
                      {t('EDIT-OUTBOUND')} <EditIcon size="md" />
                    </Button>
                  ) : undefined
                }
                key="outbound"
                legType="OUTBOUND"
                loading={loading}
                onOutboundAccordionToggle={(isOpen: boolean) => {
                  setOutboundAccordionOpen(isOpen)
                  if (isOpen) setReturnAccordionOpen(false)
                }}
                onReturnAccordionToggle={(isOpen: boolean) => {
                  setReturnAccordionOpen(isOpen)
                  if (isOpen) setOutboundAccordionOpen(false)
                }}
                outboundAccordionOpen={outboundAccordionOpen}
                ref={outboundLeg}
                returnAccordionOpen={returnAccordionOpen}
                returnLeg={false}
                selectedDepartureId={selectedDepartures?.departureOut?.id}
                selectedRouteFilters={selectedRouteFilters[0]}
                selectedType={selectedDepartures?.departureOut?.productCode}
                setSelectedRouteFilters={(value) => setSelectedRouteFilters([value, selectedRouteFilters[1]])}
                switchElement={
                  <JourneySwitch
                    attr="onewayReturnSwitch"
                    isReturnJourney={!!selectedDate?.returnDepartureDate}
                    name="returnOneWay"
                    onChange={async () => {
                      const twoLegs = !selectedDate?.returnDepartureDate
                      twoLegs
                        ? handleTwoLegs()
                        : (() => {
                            track<ButtonClick>({
                              clickedElementText: 'oneway',
                              event: 'buttonClick',
                              eventAction: 'DepartureOnewayReturnClicked',
                            })
                            setShowOnewayModal(true)
                          })()
                    }}
                  />
                }
              />
            </div>
          ) : null}

          {/* Outbound Departure single tier leg */}
          {SingleTierRoutes.includes(selectedRoutes.outbound.toUpperCase()) && outboundData && selectedDate?.outboundDepartureDate ? (
            <div data-e2e="OutboundLeg">
              <DepartureLegSingleTier
                dateChanged={debounceDateChanges}
                departureClicked={departureClicked}
                departureDate={selectedDate.outboundDepartureDate}
                departures={outboundData}
                editElement={
                  !outboundAccordionOpen ? (
                    <Button
                      aria-label="Edit outbound departures"
                      variant="ghostPrimary"
                      data-e2e="edit-outbound-leg"
                      onClick={() => setOutboundAccordionOpen(true)}
                      type="button"
                    >
                      {t('EDIT-OUTBOUND')} <EditIcon size="md" />
                    </Button>
                  ) : undefined
                }
                key="outbound"
                legType="OUTBOUND"
                loading={loading}
                onOutboundAccordionToggle={(isOpen: boolean) => {
                  setOutboundAccordionOpen(isOpen)
                  if (isOpen) setReturnAccordionOpen(false)
                }}
                onReturnAccordionToggle={(isOpen: boolean) => {
                  setReturnAccordionOpen(isOpen)
                  if (isOpen) setOutboundAccordionOpen(false)
                }}
                outboundAccordionOpen={outboundAccordionOpen}
                ref={outboundLeg}
                returnAccordionOpen={returnAccordionOpen}
                returnLeg={false}
                selectedDepartureId={selectedDepartures?.departureOut?.id}
                selectedRouteFilters={selectedRouteFilters[0]}
                selectedType={selectedDepartures?.departureOut?.productCode}
                setSelectedRouteFilters={(value) => setSelectedRouteFilters([value, selectedRouteFilters[1]])}
                switchElement={
                  <JourneySwitch
                    attr="onewayReturnSwitch"
                    isReturnJourney={!!selectedDate?.returnDepartureDate}
                    name="returnOneWay"
                    onChange={async () => {
                      const twoLegs = !selectedDate?.returnDepartureDate
                      twoLegs
                        ? handleTwoLegs()
                        : (() => {
                            track<ButtonClick>({
                              clickedElementText: 'oneway',
                              event: 'buttonClick',
                              eventAction: 'DepartureOnewayReturnClicked',
                            })
                            setShowOnewayModal(true)
                          })()
                    }}
                  />
                }
              />
            </div>
          ) : null}

          {selectedOffer.offerRLSHours && !selectedDate?.returnDepartureDate ? (
            <Banner bodyText={t('OFFER-REQUIRED-DESCRIPTION')} title={t('OFFER-REQUIRED-TITLE')} type="warning" />
          ) : null}

          {/* Other stuff */}
          {outboundData ? (
            <div className="md:mt-dfds-m bg-semantic-global-background-light-grey flex flex-col" id="ReturnLeg">
              {showRlsNoOfferSelectedBanner ? (
                <Banner
                  actionText={t('OFFER-BANNER-NO-OFFER-SELECTED-BTN')}
                  bodyText={t('OFFER-BANNER-NO-OFFER-SELECTED')}
                  closeText={t('OFFER-BANNER-INFO-BTN')}
                  onAction={() => {
                    if (returnAccordionOpen && !outboundAccordionOpen) {
                      setOutboundAccordionOpen(true)
                      setReturnAccordionOpen(false)
                      return
                    }
                    setOutboundAccordionOpen(!outboundAccordionOpen)
                  }}
                  onClose={() => {
                    setShowOfferInfoBanner(false)
                  }}
                  type="warning"
                />
              ) : null}
              {showRlsReturnDateBanner ? (
                <Banner
                  actionText={t('OFFER-BANNER-RESET-DATE-BTN')}
                  bodyText={
                    rlsOfferDateEligible
                      ? t(rlsInfoKey, {
                          offerDuration: rlsOfferDuration,
                          offerValue: selectedOffer.offerTitle,
                        })
                      : t(rlsResetDateKey, {
                          offerDuration: rlsOfferDuration,
                          offerValue: selectedOffer.offerDescription,
                        })
                  }
                  closeText={t('OFFER-BANNER-INFO-BTN')}
                  onAction={() =>
                    !rlsOfferDateEligible && selectedDepartures.departureOut?.departureDateTimeForBasket != null
                      ? debounceDateChanges(
                          format(
                            addHours(
                              new Date(selectedDepartures.departureOut?.departureDateTimeForBasket),
                              selectedOffer.offerRLSHours ?? 0
                            ),
                            'yyyy-MM-dd'
                          ),
                          2
                        )
                      : void 0
                  }
                  onClose={() => {
                    setShowOfferInfoBanner(false)
                  }}
                  type={rlsOfferDateEligible ? 'info' : 'warning'}
                />
              ) : null}
            </div>
          ) : null}

          <div ref={selectedRoutes.homebound !== 'ONE_WAY' ? returnLeg : null} />

          {selectedDate?.returnDepartureDate ? (
            <div data-e2e="ReturnLeg">
              {/* Return Departure leg */}
              {!SingleTierRoutes.includes(selectedRoutes.outbound.toUpperCase()) ? (
                <DepartureLeg
                  dateChanged={debounceDateChanges}
                  departureClicked={departureClicked}
                  departureDate={selectedDate?.returnDepartureDate}
                  departures={returnData}
                  editElement={
                    selectedDepartures.departureOut && !returnAccordionOpen ? (
                      <button
                        aria-label="Edit return departures"
                        className="button-light-ghost-primary-md"
                        data-e2e="edit-return-leg"
                        onClick={() => setReturnAccordionOpen(true)}
                        type="button"
                      >
                        {t('EDIT-RETURN')} <EditIcon size="md" />
                      </button>
                    ) : undefined
                  }
                  key="return"
                  legType="RETURN"
                  loading={loading}
                  onOutboundAccordionToggle={setOutboundAccordionOpen}
                  onReturnAccordionToggle={setReturnAccordionOpen}
                  outboundAccordionOpen={outboundAccordionOpen}
                  returnAccordionOpen={returnAccordionOpen}
                  returnLeg={true}
                  selectedDepartureId={selectedDepartures?.departureHome?.id}
                  selectedRouteFilters={selectedRouteFilters[1]}
                  selectedType={selectedDepartures?.departureHome?.productCode}
                  setSelectedRouteFilters={(value) => setSelectedRouteFilters([selectedRouteFilters[0], value])}
                />
              ) : null}

              {/* Return Departure single tier leg */}
              {SingleTierRoutes.includes(selectedRoutes.outbound.toUpperCase()) ? (
                <DepartureLegSingleTier
                  dateChanged={debounceDateChanges}
                  departureClicked={departureClicked}
                  departureDate={selectedDate?.returnDepartureDate}
                  departures={returnData}
                  editElement={
                    selectedDepartures.departureOut && !returnAccordionOpen ? (
                      <button
                        aria-label="Edit return departures"
                        className="button-light-ghost-primary-md"
                        data-e2e="edit-return-leg"
                        onClick={() => setReturnAccordionOpen(true)}
                        type="button"
                      >
                        {t('EDIT-RETURN')} <EditIcon size="md" />
                      </button>
                    ) : undefined
                  }
                  key="return"
                  legType="RETURN"
                  loading={loading}
                  onOutboundAccordionToggle={setOutboundAccordionOpen}
                  onReturnAccordionToggle={setReturnAccordionOpen}
                  outboundAccordionOpen={outboundAccordionOpen}
                  returnAccordionOpen={returnAccordionOpen}
                  returnLeg={true}
                  selectedDepartureId={selectedDepartures?.departureHome?.id}
                  selectedRouteFilters={selectedRouteFilters[1]}
                  selectedType={selectedDepartures?.departureHome?.productCode}
                  setSelectedRouteFilters={(value) => setSelectedRouteFilters([selectedRouteFilters[0], value])}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <OnewayModal
          onContinue={async (confirm: boolean) => {
            setShowOnewayModal(false)
            if (confirm) handleOneLeg()
          }}
          show={showOnewayModal}
        />
        <OverlappingModal
          model={modelForOverlappingModal}
          onContinue={(model) => {
            if (model.continue) {
              departureClicked(model.departureId, model.legNumber, model.productCode, model.continue)
            }
            setModelForOverlappingModal({ ...model, show: false })
          }}
        />
        {showBasketRecoveredInfoModal && recoveredBasketErrors ? (
          <RecoveredBasketInfoModal
            close={() => setShowBasketRecoveredInfoModal(!showBasketRecoveredInfoModal)}
            isOpen={showBasketRecoveredInfoModal}
            recoveredBasketErrors={recoveredBasketErrors}
          />
        ) : null}
        <Tracking />
      </section>
      <BackendErrorModal basketError={basketError ?? state?.basketError} />
      <OfferInvalidModal show={showOfferInvalidModal} />
      {basket ? (
        <RemoveOfferModal
          basket={basket}
          onClose={() => {
            setShowRemoveOfferModal(false)
            window.dispatchEvent(new Event('continueAborted')) // will make sure the loading state in continueHandler is reset
          }}
          show={showRemoveOfferModal}
          updateBasket={updateBasket}
        />
      ) : null}
      <DepartureSelectionsAlert message={validationMessage} onClose={clearValidationMessage} type="warning" />
      {showReturnScrollSpace ? <div aria-hidden="true" className="h-[calc(100dvh-62px)]" /> : null}
    </form>
  )
}

export default YourbookingDepartures
